import { useState } from 'react'
import {
  isFilterOption,
  FilterCategory,
  FilterOption,
  FilterOptions,
} from '../queries/types'
import { useFilterData } from '../queries/useFilterData'

type UseFiltersOptions = {
  /**
   * Set of filter options to merge into the standard filterTree.
   * Currently used to provide backwards compatibility for Asset Exports
   */
  additionalFilterOptions?: FilterOptions
  disabledCategories?: Array<FilterCategory>
  initialSelected?: Array<SelectedFilter>
  workspaceId: number
}

type SelectedFilter = {
  id: FilterOption['id']
  disabled: boolean
  value?: string
}

type Filters = {
  currentPath: Array<string>
  disableAll: boolean
  getSelectedOptions: () => FilterOptions
  lastPath: Array<string>
  loading: boolean
  onSearchChange: (value: string) => void
  options: FilterOptions
  removeAllFilters: () => void
  removeFilter: (id: FilterOption['id']) => void
  searchTerm: string
  selected: Array<SelectedFilter>
  selectFilter: (filter: SelectedFilter) => void
  setFilterValue: (
    id: FilterOption['id'],
    value: SelectedFilter['value']
  ) => void
  toggleAllFilters: (disable: boolean) => void
  toggleFilter: (id: FilterOption['id'], disable: boolean) => void
  updateFilterNavigation: (nextPath: Array<string>) => void
}

type Flatten = (
  options: FilterOptions,
  ancestors?: FilterOptions
) => FilterOptions
export const flatten: Flatten = (options, ancestors = []) =>
  options.reduce<FilterOptions>(
    (prev, cur) => [
      ...prev,
      { ...cur, ancestors: ancestors },
      ...flatten(cur.childOptions || [], ancestors.concat(cur)),
    ],
    []
  )

export const useFilters = (options: UseFiltersOptions): Filters => {
  const [currentPath, setCurrentPath] = useState<Array<string>>([])
  const [disableAll, setDisableAll] = useState<boolean>(false)
  const [lastPath, setLastPath] = useState<Array<string>>([])
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [selected, setSelected] = useState<Array<SelectedFilter>>(
    options?.initialSelected || []
  )
  const { loading, filterOptions } = useFilterData(
    options.workspaceId,
    options.disabledCategories
  )

  const allFilterOptions = [
    ...filterOptions,
    ...(options.additionalFilterOptions ?? []),
  ]

  const updateFilterNavigation: Filters['updateFilterNavigation'] = (
    nextPath
  ) => {
    setLastPath(currentPath)
    setCurrentPath(nextPath)
  }

  const onSearchChange: Filters['onSearchChange'] = (value) => {
    setSearchTerm(value)

    // Reset navigation state when searching
    setLastPath([])
    setCurrentPath([])
  }

  const selectFilter: Filters['selectFilter'] = (filter) => {
    const filterExists = selected.find(
      (selectedFilter) => selectedFilter.id === filter.id
    )
    if (filterExists) return
    setSelected([...selected, filter])
  }

  const setFilterValue: Filters['setFilterValue'] = (id, value) => {
    setSelected([
      ...selected.map((filter) => {
        if (filter.id === id) {
          return { ...filter, value }
        }

        return filter
      }),
    ])
  }

  const removeFilter: Filters['removeFilter'] = (id) => {
    setSelected(selected.filter((filter) => filter.id !== id))
  }

  const toggleFilter: Filters['toggleFilter'] = (id, disable) => {
    setSelected([
      ...selected.map((filter) => {
        if (filter.id === id) {
          return { ...filter, disabled: disable }
        }

        return filter
      }),
    ])
  }

  const removeAllFilters: Filters['removeAllFilters'] = () => setSelected([])
  const toggleAllFilters: Filters['toggleAllFilters'] = (enabled) =>
    setDisableAll(!enabled)

  const getSelectedOptions: Filters['getSelectedOptions'] = () => {
    const flattenedTree = flatten(allFilterOptions, [])
    return selected
      .map<FilterOption | null>(
        ({ id }) => flattenedTree.find((option) => option.id === id) || null
      )
      .filter<FilterOption>(isFilterOption)
  }

  return {
    currentPath,
    disableAll,
    getSelectedOptions,
    lastPath,
    loading,
    onSearchChange,
    options: allFilterOptions,
    removeAllFilters,
    removeFilter,
    searchTerm,
    selected,
    selectFilter,
    setFilterValue,
    toggleAllFilters,
    toggleFilter,
    updateFilterNavigation,
  }
}
